import React, { useState, useRef, useEffect } from 'react'
import * as pdfjsLib from 'pdfjs-dist'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import api from '../api/axios'
import { Upload, CheckCircle2, AlertTriangle, XCircle, FileText, X, Download } from 'lucide-react'

pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs'

type CheckResult = { label: string, status: 'good' | 'warning' | 'bad', detail: string }

function HealthGauge({ score, color, label }: { score: number, color: string, label: string }) {
  const radius = 70
  const strokeWidth = 14
  const circumference = Math.PI * radius
  const progress = (score / 100) * circumference
  const center = radius + strokeWidth

  return (
    <div className="flex flex-col items-center">
      <svg width={center * 2} height={center + strokeWidth} viewBox={`0 0 ${center * 2} ${center + strokeWidth}`}>
        <path
          d={`M ${strokeWidth / 2} ${center} A ${radius} ${radius} 0 0 1 ${center * 2 - strokeWidth / 2} ${center}`}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
        <path
          d={`M ${strokeWidth / 2} ${center} A ${radius} ${radius} 0 0 1 ${center * 2 - strokeWidth / 2} ${center}`}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={`${progress} ${circumference}`}
          style={{ transition: 'stroke-dasharray 0.6s ease' }}
        />
      </svg>
      <div className="text-center -mt-10">
        <p className="font-display text-4xl font-bold" style={{ color }}>{score}</p>
        <p className="text-xs text-gray-400">/ 100</p>
      </div>
      <p className="text-sm font-semibold mt-1" style={{ color }}>{label}</p>
    </div>
  )
}

export default function ArtworkHealthCheckPage() {
  const [fileName, setFileName] = useState<string | null>(null)
  const [fileType, setFileType] = useState<string | null>(null)
  const [analyzing, setAnalyzing] = useState(false)
  const [results, setResults] = useState<CheckResult[] | null>(null)
  const [needsSize, setNeedsSize] = useState(false)
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const [intendedWidth, setIntendedWidth] = useState('')
  const [showContactForm, setShowContactForm] = useState(false)
  const [contactForm, setContactForm] = useState({ name: '', email: '', companyName: '', message: '' })
  const [contactSubmitted, setContactSubmitted] = useState(false)
  const [contactSubmitting, setContactSubmitting] = useState(false)
  const [autoInquiryId, setAutoInquiryId] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    document.title = 'Artwork Health Checker — Inkcredible Studios'
  }, [])

  const reset = () => {
    setFileName(null)
    setFileType(null)
    setOriginalFile(null)
    setResults(null)
    setIntendedWidth('')
    setImgDimensions(null)
    setShowContactForm(false)
    setContactSubmitted(false)
  }

  const handleFileSelect = (file: File) => {
    setFileName(file.name)
    setOriginalFile(file)
    const ext = file.name.split('.').pop()?.toLowerCase() || ''
    setFileType(ext)
    setResults(null)

    if (ext === 'png' || ext === 'jpg' || ext === 'jpeg') {
      analyzeRaster(file)
      return
    }
    if (ext === 'pdf') {
      analyzePdf(file)
      return
    }
    if (ext === 'svg') {
      analyzeSvg(file)
      return
    }
    setResults([{ label: 'File type', status: 'bad', detail: 'Unsupported file type. Please upload a PDF, SVG, PNG, or JPG file.' }])
  }

  const [imgDimensions, setImgDimensions] = useState<{ w: number, h: number } | null>(null)

  const buildRasterChecks = (naturalWidth: number, naturalHeight: number, widthInches: number): CheckResult[] => {
    const dpi = Math.round(naturalWidth / widthInches)
    return [
      {
        label: 'File format',
        status: 'warning',
        detail: `This is a raster (pixel-based) image, not a vector file. Vector formats (AI, EPS, SVG, PDF) are preferred for production since they scale without quality loss.`
      },
      {
        label: 'Resolution at intended size',
        status: dpi >= 300 ? 'good' : dpi >= 150 ? 'warning' : 'bad',
        detail: `At ${widthInches}" wide, this image is approximately ${dpi} DPI (${naturalWidth} × ${naturalHeight}px). ${dpi >= 300 ? 'This meets the 300 DPI standard for sharp production output.' : dpi >= 150 ? 'This is below the recommended 300 DPI and may appear soft at this size.' : 'This is well below production quality and will likely appear blurry or pixelated at this size.'}`
      }
    ]
  }

  const analyzeRaster = (file: File) => {
    setAnalyzing(true)
    const img = new Image()
    const url = URL.createObjectURL(file)
    img.onload = () => {
      setImgDimensions({ w: img.naturalWidth, h: img.naturalHeight })
      const defaultWidth = '2'
      setIntendedWidth(defaultWidth)
      setResults(buildRasterChecks(img.naturalWidth, img.naturalHeight, parseFloat(defaultWidth)))
      setAnalyzing(false)
      URL.revokeObjectURL(url)
    }
    img.src = url
  }

  const handleWidthChange = (value: string) => {
    setIntendedWidth(value)
    const widthNum = parseFloat(value)
    if (!imgDimensions || isNaN(widthNum) || widthNum <= 0) return
    setResults(buildRasterChecks(imgDimensions.w, imgDimensions.h, widthNum))
  }

  const analyzeSvg = async (file: File) => {
    setAnalyzing(true)
    const text = await file.text()
    const checks: CheckResult[] = []
    checks.push({ label: 'File format', status: 'good', detail: 'SVG is a vector format — suitable for production.' })

    const hasLiveText = /<text[\s>]/i.test(text)
    checks.push({
      label: 'Text status',
      status: hasLiveText ? 'warning' : 'good',
      detail: hasLiveText
        ? 'This file contains live, editable text. Live text can shift or break if opened without the original font installed — text should be outlined/converted to shapes before production.'
        : 'No live text detected — text appears to already be outlined as shapes, which is ideal for production.'
    })

    const fillColors = new Set<string>()
    const fillMatches = Array.from(text.matchAll(/fill=["']([^"']+)["']/gi))
    for (let i = 0; i < fillMatches.length; i++) {
      const m = fillMatches[i]
      if (m[1] && m[1] !== 'none') fillColors.add(m[1].toLowerCase())
    }
    checks.push({
      label: 'Color count',
      status: 'good',
      detail: `Detected approximately ${fillColors.size} distinct fill color${fillColors.size === 1 ? '' : 's'} in this file.`
    })

    let svgOtherCount = 0
    let svgHasNeutral = false
    fillColors.forEach(c => {
      const hexMatch = c.match(/^#[0-9a-f]{6}$/i)
      if (hexMatch) {
        const neutral = isNeutralColor(c)
        if (neutral) svgHasNeutral = true
        else svgOtherCount++
      } else {
        svgOtherCount++
      }
    })
    const svgNeutralNote = svgHasNeutral ? ` (white and/or black also detected, shown separately as they are not production-sensitive)` : ''

    checks.push({
      label: 'Color usage note',
      status: 'good',
      detail: `${svgOtherCount} non-neutral color${svgOtherCount === 1 ? '' : 's'}${svgNeutralNote} found in this file. SVG does not support named spot/Pantone color definitions — all colors here are plain RGB/hex values.`
    })

    setResults(checks)
    setAnalyzing(false)
  }

  const isNeutralColor = (hex: string): 'white' | 'black' | null => {
    const clean = hex.replace('#', '').toLowerCase()
    if (clean.length !== 6) return null
    const r = parseInt(clean.substring(0, 2), 16)
    const g = parseInt(clean.substring(2, 4), 16)
    const b = parseInt(clean.substring(4, 6), 16)
    const threshold = 12
    if (r >= 255 - threshold && g >= 255 - threshold && b >= 255 - threshold) return 'white'
    if (r <= threshold && g <= threshold && b <= threshold) return 'black'
    return null
  }

  const collectPageColors = (opList: any): Set<string> => {
    const colors = new Set<string>()
    try {
      const ops = opList.fnArray
      const args = opList.argsArray
      const OPS = pdfjsLib.OPS

      for (let i = 0; i < ops.length; i++) {
        const op = ops[i]
        if (op === OPS.setFillRGBColor || op === OPS.setStrokeRGBColor) {
          const val = args[i][0]
          if (typeof val === 'string') {
            colors.add(val.toLowerCase())
          } else if (Array.isArray(val) && val.length >= 3) {
            const toHex = (n: number) => Math.round(n * 255).toString(16).padStart(2, '0')
            colors.add(`#${toHex(val[0])}${toHex(val[1])}${toHex(val[2])}`)
          }
        }
      }
    } catch (_) {}
    return colors
  }

  const buildColorChecks = (allColors: Set<string>): CheckResult[] => {
    const checks: CheckResult[] = []
    let whiteFound = false
    let blackFound = false
    let otherCount = 0

    allColors.forEach(hex => {
      const neutral = isNeutralColor(hex)
      if (neutral === 'white') whiteFound = true
      else if (neutral === 'black') blackFound = true
      else otherCount++
    })

    const totalColors = allColors.size
    const neutralNote = (whiteFound || blackFound)
      ? ` (white and/or black also detected, shown separately as they are not production-sensitive)`
      : ''

    checks.push({
      label: 'Color count',
      status: 'good',
      detail: `${totalColors} distinct color${totalColors === 1 ? '' : 's'} detected across this document — ${otherCount} non-neutral color${otherCount === 1 ? '' : 's'}${neutralNote}. This tool cannot confirm whether these are defined as Pantone/spot colors or plain RGB/CMYK — please verify color definitions with your production team if exact color matching matters.`
    })
    return checks
  }

  const analyzePdf = async (file: File) => {
    setAnalyzing(true)
    const checks: CheckResult[] = []
    try {
      const arrayBuffer = await file.arrayBuffer()
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise
      const OPS = pdfjsLib.OPS
      const numPages = pdf.numPages

      checks.push({ label: 'File format', status: 'good', detail: `Valid PDF document with ${numPages} page${numPages === 1 ? '' : 's'} detected.` })

      let pagesWithVector = 0
      const allColors = new Set<string>()
      let pagesWithLiveText = 0
      let pagesWithImages = 0
      let pagesWithNoContent = 0
      let totalLiveTextElements = 0
      let totalImageCount = 0
      const allDpiEstimates: number[] = []
      let dpiUndeterminedCount = 0

      for (let pageNum = 1; pageNum <= numPages; pageNum++) {
        const page = await pdf.getPage(pageNum)
        const opList = await page.getOperatorList()
        const ops = opList.fnArray

        const hasVectorPaths = ops.some((op: number) => op === OPS.constructPath || op === OPS.fill || op === OPS.stroke || op === OPS.eoFill)
        const imageOps = ops.filter((op: number) => op === OPS.paintImageXObject || op === OPS.paintInlineImageXObject).length
        const textContent = await page.getTextContent()
        const hasLiveText = textContent.items.length > 0
        const hasAnyContent = hasVectorPaths || hasLiveText || imageOps > 0

        if (hasVectorPaths) pagesWithVector++
        if (hasLiveText) { pagesWithLiveText++; totalLiveTextElements += textContent.items.length }
        const pageColors = collectPageColors(opList)
        pageColors.forEach(c => allColors.add(c))
        if (imageOps > 0) { pagesWithImages++; totalImageCount += imageOps }
        if (!hasAnyContent) pagesWithNoContent++

        if (imageOps > 0) {
          try {
            let currentMatrix = [1, 0, 0, 1, 0, 0]
            const matrixStack: number[][] = []
            let foundAnyDpi = false
            for (let i = 0; i < ops.length; i++) {
              const op = ops[i]
              if (op === OPS.save) matrixStack.push([...currentMatrix])
              if (op === OPS.restore) currentMatrix = matrixStack.pop() || currentMatrix
              if (op === OPS.transform) {
                const args = opList.argsArray[i]
                currentMatrix = [args[0], args[1], args[2], args[3], args[4], args[5]]
              }
              if (op === OPS.paintImageXObject) {
                const placedWidthPt = Math.abs(currentMatrix[0])
                const placedWidthIn = placedWidthPt / 72
                const imgName = opList.argsArray[i][0]
                try {
                  const imgObj: any = await new Promise((resolve) => page.objs.get(imgName, resolve))
                  if (imgObj && imgObj.width && placedWidthIn > 0) {
                    allDpiEstimates.push(Math.round(imgObj.width / placedWidthIn))
                    foundAnyDpi = true
                  }
                } catch (_) {}
              }
            }
            if (!foundAnyDpi) dpiUndeterminedCount++
          } catch (_) {
            dpiUndeterminedCount++
          }
        }
      }

      checks.push(...buildColorChecks(allColors))

      const pageNote = numPages > 1 ? ` (checked across all ${numPages} pages)` : ''

      if (pagesWithNoContent === numPages) {
        checks.push({
          label: 'Design content',
          status: 'bad',
          detail: `No design content (vector paths, text, or images) was detected on ${numPages === 1 ? 'this page' : 'any page'}. This file appears to be blank or empty — please verify the correct file was uploaded.`
        })
      } else if (pagesWithNoContent > 0) {
        checks.push({
          label: 'Design content',
          status: 'warning',
          detail: `${pagesWithNoContent} of ${numPages} pages appear to have no design content at all. Please verify all pages were intended to be included.`
        })
      }

      checks.push({
        label: 'Vector content',
        status: pagesWithVector > 0 ? 'good' : 'warning',
        detail: pagesWithVector > 0
          ? `Vector paths detected${pageNote}${numPages > 1 ? ` — ${pagesWithVector} of ${numPages} pages contain vector artwork` : ''}.`
          : `No vector paths detected${pageNote}. This file may be a flattened/scanned image wrapped in a PDF, which limits scalability and editability.`
      })

      checks.push({
        label: 'Text status',
        status: pagesWithLiveText > 0 ? 'warning' : (pagesWithVector > 0 ? 'good' : 'warning'),
        detail: pagesWithLiveText > 0
          ? `Live, extractable text found on ${pagesWithLiveText} of ${numPages} page${numPages === 1 ? '' : 's'} (${totalLiveTextElements} text element${totalLiveTextElements === 1 ? '' : 's'} total). If fonts aren't embedded, this text may not display correctly on another machine. For production, text should be outlined to shapes.`
          : pagesWithVector > 0
            ? `No live text detected${pageNote} — any visible lettering appears to already be outlined as vector shapes, which is ideal for production.`
            : `No live text and no vector content detected${pageNote}. This file may be blank, a flattened raster image, or have no extractable design content — please double check the file before sending it for production.`
      })

      if (totalImageCount > 0) {
        if (allDpiEstimates.length > 0) {
          const minDpi = Math.min(...allDpiEstimates)
          checks.push({
            label: 'Raster image resolution',
            status: minDpi >= 300 ? 'good' : minDpi >= 150 ? 'warning' : 'bad',
            detail: `${totalImageCount} raster image${totalImageCount === 1 ? '' : 's'} found across ${pagesWithImages} of ${numPages} page${numPages === 1 ? '' : 's'}, with the lowest estimated at ${minDpi} DPI at placed size. ${minDpi >= 300 ? 'This meets production-quality resolution.' : minDpi >= 150 ? 'This is below the recommended 300 DPI and may appear soft in production.' : 'This is well below production quality and will likely appear blurry or pixelated.'} Note: PDFs always contain image data internally, so this checks resolution only — it cannot detect whether the original source file (e.g. in Illustrator) used a linked image.`
          })
        } else {
          checks.push({
            label: 'Raster images',
            status: 'warning',
            detail: `${totalImageCount} raster image${totalImageCount === 1 ? '' : 's'} found inside this PDF, but resolution at placed size could not be determined automatically. Please verify image quality manually before production.`
          })
        }
      } else {
        checks.push({
          label: 'Embedded images',
          status: pagesWithVector > 0 ? 'good' : 'warning',
          detail: pagesWithVector > 0
            ? `No embedded raster images found${pageNote} — content appears to be fully vector.`
            : `No embedded raster images found, but no vector content was found either${pageNote}. This may be blank or empty.`
        })
      }
    } catch (e: any) {
      console.error('[PDF analysis error]', e)
      checks.push({ label: 'File analysis', status: 'bad', detail: `Could not fully analyze this PDF. ${e?.message || 'It may be corrupted, password-protected, or in an unsupported PDF version.'}` })
    }
    setResults(checks)
    setAnalyzing(false)
  }

  const failedChecks = results?.filter(r => r.status !== 'good') || []

  useEffect(() => {
    if (!results || results.length === 0) return
    api.post('/contact/auto-log', {
      source: 'artwork_health_check',
      metadata: { fileName, fileType, failedChecks: failedChecks.map(c => c.label), healthScore }
    }).then(res => {
      if (res.data?.id) setAutoInquiryId(res.data.id)
    }).catch(() => {})
  }, [results])

  const hasLiveTextWarning = results?.some(r => r.label === 'Text status' && r.status === 'warning' && r.detail.includes('Live, extractable text found'))
  const hasNoContentAtAll = results?.some(r => r.label === 'Design content' && r.status === 'bad')

  const healthScore = results && results.length > 0
    ? hasNoContentAtAll
      ? 2
      : Math.round(results.reduce((sum, r) => {
          if (r.label === 'Text status' && r.status === 'warning' && r.detail.includes('Live, extractable text found')) return sum + 35
          return sum + (r.status === 'good' ? 100 : r.status === 'warning' ? 55 : 0)
        }, 0) / results.length)
    : null

  const scoreColor = healthScore === null ? '#9ca3af' : healthScore >= 80 ? '#22c55e' : healthScore >= 50 ? '#f59e0b' : '#ef4444'
  const scoreLabel = healthScore === null ? '' : healthScore >= 80 ? 'Production Ready' : healthScore >= 50 ? 'Needs Attention' : 'Not Production Ready'

  const [attachFile, setAttachFile] = useState(true)
  const [originalFile, setOriginalFile] = useState<File | null>(null)

  const handleContactSubmit = async () => {
    if (!contactForm.name || !contactForm.email) return
    setContactSubmitting(true)
    try {
      const formData = new FormData()
      formData.append('source', 'artwork_health_check')
      formData.append('name', contactForm.name)
      formData.append('email', contactForm.email)
      formData.append('companyName', contactForm.companyName)
      formData.append('message', contactForm.message)
      formData.append('metadata', JSON.stringify({ fileName, fileType, failedChecks: failedChecks.map(c => c.label), healthScore }))
      if (autoInquiryId) formData.append('inquiryId', autoInquiryId)
      if (attachFile && originalFile) formData.append('file', originalFile)

      await api.post('/contact', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
      setContactSubmitted(true)
    } catch (_) {
      setContactSubmitted(true)
    } finally {
      setContactSubmitting(false)
    }
  }

  const handleDownloadReport = async () => {
    if (!results || healthScore === null) return
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
    const pageWidth = doc.internal.pageSize.getWidth()

    const logoUrl = '/New Inkcredible Logo.webp'
    try {
      const { imgData, imgAspect } = await new Promise<{ imgData: string, imgAspect: number }>((resolve, reject) => {
        const img = new Image()
        img.crossOrigin = 'anonymous'
        img.onload = () => {
          const canvas = document.createElement('canvas')
          canvas.width = img.width
          canvas.height = img.height
          const ctx = canvas.getContext('2d')!
          ctx.drawImage(img, 0, 0)
          resolve({ imgData: canvas.toDataURL('image/png'), imgAspect: img.height / img.width })
        }
        img.onerror = reject
        img.src = logoUrl
      })

      doc.setFillColor(7, 24, 41)
      doc.rect(0, 0, pageWidth, 32, 'F')

      const logoW = 55
      const logoH = logoW * imgAspect
      doc.addImage(imgData, 'PNG', 6, 4, logoW, logoH)

      doc.setTextColor(200, 215, 230)
      doc.setFontSize(9)
      doc.setFont('helvetica', 'normal')
      doc.text('Artwork File Health Report', 8, 27)
    } catch (_) {
      doc.setFillColor(7, 24, 41)
      doc.rect(0, 0, pageWidth, 20, 'F')
      doc.setTextColor(255, 255, 255)
      doc.setFontSize(14)
      doc.text('Artwork File Health Report', 8, 13)
    }

    let y = 42
    doc.setTextColor(30, 41, 59)
    doc.setFontSize(11)
    doc.setFont('helvetica', 'bold')
    doc.text(`File: ${fileName || 'Unknown'}`, 8, y)
    y += 6
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(9)
    doc.setTextColor(120, 130, 140)
    doc.text(`Generated ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, 8, y)
    y += 10

    doc.setFontSize(24)
    doc.setFont('helvetica', 'bold')
    const scoreColorRgb = healthScore >= 80 ? [34, 197, 94] : healthScore >= 50 ? [245, 158, 11] : [239, 68, 68]
    doc.setTextColor(scoreColorRgb[0], scoreColorRgb[1], scoreColorRgb[2])
    doc.text(`${healthScore}/100`, 8, y)
    doc.setFontSize(12)
    doc.text(scoreLabel, 45, y)
    y += 10

    autoTable(doc, {
      startY: y,
      head: [['Check', 'Status', 'Details']],
      body: results.map(r => [r.label, r.status === 'good' ? 'Good' : r.status === 'warning' ? 'Warning' : 'Issue', r.detail]),
      styles: { fontSize: 8, cellPadding: 3 },
      headStyles: { fillColor: [7, 24, 41], textColor: [255, 255, 255] },
      columnStyles: { 0: { cellWidth: 32 }, 1: { cellWidth: 18 }, 2: { cellWidth: 'auto' } },
      didParseCell: (data) => {
        if (data.column.index === 1 && data.row.section === 'body') {
          const val = data.cell.raw
          if (val === 'Good') data.cell.styles.textColor = [34, 197, 94]
          if (val === 'Warning') data.cell.styles.textColor = [245, 158, 11]
          if (val === 'Issue') data.cell.styles.textColor = [239, 68, 68]
        }
      }
    })

    const finalY = (doc as any).lastAutoTable.finalY || y
    doc.setFontSize(8)
    doc.setTextColor(150, 150, 150)
    doc.text('This is an automated preliminary check and may not catch every production issue.', 8, finalY + 8)
    doc.text('Inkcredible Studios — inkcrediblestudios.com', 8, finalY + 13)

    doc.save(`artwork-health-report-${(fileName || 'file').replace(/\.[^.]+$/, '')}.pdf`)
  }

  const statusIcon = (status: CheckResult['status']) => {
    if (status === 'good') return <CheckCircle2 size={18} className="text-green-500 flex-shrink-0" />
    if (status === 'warning') return <AlertTriangle size={18} className="text-amber-500 flex-shrink-0" />
    return <XCircle size={18} className="text-red-500 flex-shrink-0" />
  }

  return (
    <div className="min-h-screen bg-[#fdfcfb] dark:bg-[#0a1929]">
      <div className="border-b border-gray-100 dark:border-[#1e3450] bg-white dark:bg-[#0f2137] px-4 sm:px-8 py-3.5">
        <img src="/Full_Color_Logo.svg" alt="Inkcredible Studios" className="h-7 object-contain dark:hidden" />
        <img src="/White_Color_Logo.svg" alt="Inkcredible Studios" className="h-7 object-contain hidden dark:block" />
      </div>

      <div className="max-w-3xl mx-auto p-4 sm:p-8">
        <div className="mb-6">
          <h1 className="font-display text-3xl font-bold text-navy-900 dark:text-white">Artwork File Health Checker</h1>
          <p className="text-gray-400 mt-1">Upload your artwork file and get an instant production-readiness check</p>
        </div>

        {!fileName ? (
          <label className="block border-2 border-dashed border-gray-200 dark:border-[#1e3450] rounded-2xl py-16 text-center cursor-pointer hover:border-orange-300 transition-colors">
            <input ref={fileInputRef} type="file" accept=".pdf,.svg,.png,.jpg,.jpeg" onChange={e => { const f = e.target.files?.[0]; if (f) handleFileSelect(f) }} className="hidden" />
            <Upload size={28} className="mx-auto text-gray-300 mb-3" />
            <p className="text-sm text-gray-500 font-medium">Click to upload your artwork file</p>
            <p className="text-xs text-gray-300 mt-1">Supports PDF, SVG, PNG, JPG — checked entirely in your browser</p>
          </label>
        ) : (
          <div className="rounded-2xl border border-gray-100 dark:border-[#1e3450] bg-white dark:bg-[#0f2137] overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100 dark:border-[#1e3450]">
              <div className="flex items-center gap-2.5">
                <FileText size={16} className="text-orange-500" />
                <span className="text-sm font-medium text-navy-800 dark:text-white truncate max-w-[300px]">{fileName}</span>
              </div>
              <button onClick={reset} className="text-gray-300 hover:text-gray-500 transition-colors">
                <X size={16} />
              </button>
            </div>

            <div className="p-5">
              {imgDimensions && (
                <div className="mb-4 bg-gray-50 dark:bg-[#112240] rounded-2xl p-5 text-center space-y-3">
                  <p className="text-xs text-gray-400">Native size: {imgDimensions.w} × {imgDimensions.h} pixels</p>
                  {intendedWidth && !isNaN(parseFloat(intendedWidth)) && parseFloat(intendedWidth) > 0 && (
                    <div>
                      <p className={`font-display text-5xl font-bold leading-none ${Math.round(imgDimensions.w / parseFloat(intendedWidth)) >= 300 ? 'text-green-500' : Math.round(imgDimensions.w / parseFloat(intendedWidth)) >= 150 ? 'text-amber-500' : 'text-red-500'}`}>
                        {Math.round(imgDimensions.w / parseFloat(intendedWidth))}
                      </p>
                      <p className="text-sm text-gray-400 mt-1">DPI at {intendedWidth}" wide</p>
                    </div>
                  )}
                  <div className="flex items-center justify-center gap-2 pt-2">
                    <label className="text-sm text-navy-700 dark:text-gray-300">Adjust intended output width:</label>
                    <input
                      type="number"
                      step="0.1"
                      value={intendedWidth}
                      onChange={e => handleWidthChange(e.target.value)}
                      className="w-24 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-[#1e3450] bg-white dark:bg-[#0f2137] text-navy-800 dark:text-white text-sm text-center focus:outline-none focus:ring-2 focus:ring-orange-200"
                    />
                    <span className="text-sm text-gray-400">inches</span>
                  </div>
                </div>
              )}

              {analyzing && <p className="text-sm text-gray-400 py-6 text-center">Analyzing file...</p>}

              {results && (
                <div className="space-y-3">
                  {healthScore !== null && (
                    <div className="flex flex-col items-center py-2 mb-2">
                      <HealthGauge score={healthScore} color={scoreColor} label={scoreLabel} />
                      {hasLiveTextWarning && (
                        <p className="text-xs text-amber-600 dark:text-amber-400 font-medium mt-2 text-center max-w-sm">
                          ⚠ This file still has live, editable text. Outline the text to shapes before sending to production, even if the overall score looks high.
                        </p>
                      )}
                    </div>
                  )}
                  {results.map((r, i) => (
                    <div key={i} className={`flex items-start gap-3 rounded-xl px-4 py-3 ${r.status === 'warning' ? 'bg-amber-50 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-900/20' : r.status === 'bad' ? 'bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/20' : 'bg-gray-50 dark:bg-[#112240]'}`}>
                      {statusIcon(r.status)}
                      <div>
                        <p className="text-sm font-semibold text-navy-800 dark:text-white">{r.label}</p>
                        <p className={`text-xs mt-0.5 leading-relaxed ${r.status === 'warning' ? 'text-amber-700 dark:text-amber-300 font-medium' : r.status === 'bad' ? 'text-red-700 dark:text-red-300 font-medium' : 'text-gray-400'}`}>{r.detail}</p>
                      </div>
                    </div>
                  ))}

                  <div className="pt-4 border-t border-gray-100 dark:border-[#1e3450] mt-4">
                    {failedChecks.length > 0 ? (
                      <p className="text-sm text-navy-700 dark:text-gray-300 mb-4">
                        We found {failedChecks.length} thing{failedChecks.length === 1 ? '' : 's'} worth fixing before this file goes to production. Want us to take care of it?
                      </p>
                    ) : (
                      <p className="text-sm text-navy-700 dark:text-gray-300 mb-4">
                        This file looks production-ready. If you'd like a second opinion or need it adapted for a specific product, we're happy to help.
                      </p>
                    )}

                    {!showContactForm && !contactSubmitted && (
                      <div className="flex flex-wrap gap-3">
                        <button onClick={() => setShowContactForm(true)} className="px-4 py-2.5 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold transition-colors">
                          Get it fixed — send us your file
                        </button>
                        <a href="/trial" className="px-4 py-2.5 rounded-xl border border-gray-200 dark:border-[#1e3450] text-navy-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-50 dark:hover:bg-[#1c3a5e] transition-colors">
                          Start a free subscription trial
                        </a>
                        <button onClick={handleDownloadReport} className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-gray-200 dark:border-[#1e3450] text-navy-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-50 dark:hover:bg-[#1c3a5e] transition-colors">
                          <Download size={14} /> Download report
                        </button>
                      </div>
                    )}

                    {showContactForm && !contactSubmitted && (
                      <div className="space-y-3 bg-gray-50 dark:bg-[#112240] rounded-xl p-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <input placeholder="Your name" value={contactForm.name} onChange={e => setContactForm(f => ({ ...f, name: e.target.value }))}
                            className="px-3 py-2 rounded-lg border border-gray-200 dark:border-[#1e3450] bg-white dark:bg-[#0f2137] text-sm text-navy-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-200" />
                          <input placeholder="Email address" value={contactForm.email} onChange={e => setContactForm(f => ({ ...f, email: e.target.value }))}
                            className="px-3 py-2 rounded-lg border border-gray-200 dark:border-[#1e3450] bg-white dark:bg-[#0f2137] text-sm text-navy-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-200" />
                        </div>
                        <input placeholder="Company name (optional)" value={contactForm.companyName} onChange={e => setContactForm(f => ({ ...f, companyName: e.target.value }))}
                          className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-[#1e3450] bg-white dark:bg-[#0f2137] text-sm text-navy-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-200" />
                        <textarea placeholder="Anything else we should know?" rows={3} value={contactForm.message} onChange={e => setContactForm(f => ({ ...f, message: e.target.value }))}
                          className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-[#1e3450] bg-white dark:bg-[#0f2137] text-sm text-navy-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-200" />
                        <label className="flex items-center gap-2 text-xs text-navy-700 dark:text-gray-300 cursor-pointer">
                          <input type="checkbox" checked={attachFile} onChange={e => setAttachFile(e.target.checked)} className="accent-orange-500" />
                          Attach my file ({fileName}) so your team can take a closer look
                        </label>
                        <button onClick={handleContactSubmit} disabled={contactSubmitting || !contactForm.name || !contactForm.email}
                          className="px-4 py-2.5 rounded-xl bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white text-sm font-semibold transition-colors">
                          {contactSubmitting ? 'Sending...' : 'Send inquiry'}
                        </button>
                      </div>
                    )}

                    {contactSubmitted && (
                      <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 dark:bg-green-900/10 rounded-xl px-4 py-3">
                        <CheckCircle2 size={16} />
                        <span>Thanks! We'll be in touch shortly.</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <p className="text-xs text-gray-400 mt-8 text-center">
          This is an automated preliminary check based on common production requirements and may not catch every issue. Your file is analyzed entirely in your browser and is never uploaded to our servers.
        </p>
      </div>
    </div>
  )
}
